'use client';

import React, { useState, useMemo, useCallback } from 'react';
import {
  Upload,
  Loader2,
  Camera,
  FileText,
  Sparkles,
  X,
} from 'lucide-react';
import { DocumentImageViewer, type DocumentPreviewItem } from '@/components/shared/finance/DocumentImageViewer';
import {
  type DocumentPhotoAttachment,
  MAX_ATTACHMENT_COUNT,
  MAX_ATTACHMENT_FILE_SIZE_BYTES,
  MAX_ATTACHMENT_TOTAL_BYTES,
  OCR_SAFE_TOTAL_CONFIDENCE_THRESHOLD,
  SALES_CONFIRM_SIGNAL_CONFIDENCE_THRESHOLD,
  ATTACHMENT_ANALYSIS_ACCEPT_PURCHASE,
  ATTACHMENT_ANALYSIS_ACCEPT_SALES,
  formatAttachmentFileSize,
  isImageLikeAttachment,
  getAttachmentConfidence,
  buildDetectedSignalLabel,
} from './documentModalHelpers';
import {
  getSalesIntakeSourceLabel,
  getSalesIntakeTargetLabel,
} from '@/lib/finance/salesIntakeAnalysis';

export interface PhotoSidePanelProps {
  photoAttachments: DocumentPhotoAttachment[];
  isDarkMode: boolean;
  isPurchaseDocument: boolean;
  loadingPersistedAttachments: boolean;
  ocrProcessing: boolean;
  isAttachmentDragActive: boolean;
  currencySymbol: string;
  formatCurrency: (val: number | string | undefined | null) => string;
  onPhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAttachmentDragOver: (e: React.DragEvent<HTMLLabelElement>) => void;
  onAttachmentDragLeave: (e: React.DragEvent<HTMLLabelElement>) => void;
  onAttachmentDrop: (e: React.DragEvent<HTMLLabelElement>) => void;
  onRemovePhoto: (photoId: string) => void;
  onApplyOcrTotal: (photoId: string) => void;
  onApplyOcrDate: (photoId: string) => void;
  onApplySalesCustomerPo: (photoId: string) => void;
  onRerunAttachmentAnalysis: (photoId: string) => void;
  onOpenFullViewer?: (index: number) => void;
}

export default function PhotoSidePanel({
  photoAttachments,
  isDarkMode,
  isPurchaseDocument,
  loadingPersistedAttachments,
  ocrProcessing,
  isAttachmentDragActive,
  currencySymbol,
  formatCurrency,
  onPhotoUpload,
  onAttachmentDragOver,
  onAttachmentDragLeave,
  onAttachmentDrop,
  onRemovePhoto,
  onApplyOcrTotal,
  onApplyOcrDate,
  onApplySalesCustomerPo,
  onRerunAttachmentAnalysis,
  onOpenFullViewer,
}: PhotoSidePanelProps) {
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);

  const formatCurrencyWithSymbol = (value: number | string | undefined | null): string => {
    return `${currencySymbol} ${formatCurrency(value)}`;
  };

  // Build preview items for DocumentImageViewer (only image-like and PDF attachments)
  const previewItems = useMemo<DocumentPreviewItem[]>(() => {
    return photoAttachments
      .filter((photo) => {
        const previewSource = photo.data_url || photo.file_path;
        return previewSource && (isImageLikeAttachment(photo.file_name, photo.mime_type) || photo.file_type === 'pdf');
      })
      .map((photo) => ({
        url: photo.data_url || photo.file_path || '',
        fileType: photo.file_type || null,
        fileName: photo.file_name,
      }));
  }, [photoAttachments]);

  // Map photoAttachments index to previewItems index (since not all attachments are viewable)
  const photoIdxToPreviewIdx = useMemo(() => {
    const map: Record<number, number> = {};
    let previewIdx = 0;
    photoAttachments.forEach((photo, i) => {
      const previewSource = photo.data_url || photo.file_path;
      if (previewSource && (isImageLikeAttachment(photo.file_name, photo.mime_type) || photo.file_type === 'pdf')) {
        map[i] = previewIdx++;
      }
    });
    return map;
  }, [photoAttachments]);

  const handleOpenFullViewer = useCallback((idx: number) => {
    onOpenFullViewer?.(idx);
  }, [onOpenFullViewer]);

  // Clamp activePhotoIdx when attachments change
  const clampedPhotoIdx = photoAttachments.length > 0 ? Math.min(activePhotoIdx, photoAttachments.length - 1) : 0;
  if (photoAttachments.length > 0 && activePhotoIdx !== clampedPhotoIdx) {
    setActivePhotoIdx(clampedPhotoIdx);
  }

  return (
    <div className={`flex h-full flex-col ${isDarkMode ? 'bg-gray-900/70' : 'bg-gray-50/90'}`}>
      {/* Header */}
      <div className={`border-b px-4 py-3 ${isDarkMode ? 'border-gray-700 bg-gray-900/90' : 'border-gray-200 bg-white/90'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
            <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Files &amp; OCR
            </span>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {photoAttachments.length} file{photoAttachments.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Document Image Viewer — shows selected photo with zoom/rotate/enlarge */}
        {previewItems.length > 0 && (
          <div data-photo-viewer className="mb-4 flex flex-col rounded-xl overflow-hidden border" style={{ height: '40vh', minHeight: '300px' }}>
            <DocumentImageViewer
              items={previewItems}
              isDark={isDarkMode}
              onOpenFullViewer={handleOpenFullViewer}
            />
          </div>
        )}

        {/* Upload Button */}
        <div className="mb-4">
          <label
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${isAttachmentDragActive
              ? isDarkMode
                ? 'border-emerald-400 bg-emerald-900/25'
                : 'border-emerald-500 bg-emerald-50'
              : isDarkMode
                ? 'border-gray-600 hover:border-emerald-500 hover:bg-emerald-900/20'
                : 'border-gray-300 hover:border-emerald-500 hover:bg-emerald-50'
              }`}
            onDragEnter={onAttachmentDragOver}
            onDragOver={onAttachmentDragOver}
            onDragLeave={onAttachmentDragLeave}
            onDrop={onAttachmentDrop}
          >
            <Upload className={`h-5 w-5 ${isAttachmentDragActive ? 'text-emerald-500' : 'text-emerald-600'}`} />
            <div>
              <div className={`text-xs font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {isAttachmentDragActive ? 'Drop files here' : 'Browse or drag files'}
              </div>
              <div className={`mt-1 text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {isPurchaseDocument
                  ? 'Images: receipt OCR · PDF/CSV/OFX: structured parsing'
                  : 'Images: OCR + sales heuristics · PDF/TXT/XLSX: sales intake'}
              </div>
            </div>
            <input
              type="file"
              accept={isPurchaseDocument ? ATTACHMENT_ANALYSIS_ACCEPT_PURCHASE : ATTACHMENT_ANALYSIS_ACCEPT_SALES}
              multiple
              onChange={onPhotoUpload}
              className="hidden"
              disabled={ocrProcessing || loadingPersistedAttachments}
            />
          </label>
        </div>

        {/* Loading state */}
        {loadingPersistedAttachments && photoAttachments.length === 0 && (
          <div className={`mb-4 flex items-center justify-center gap-2 rounded-lg border px-4 py-4 text-xs ${isDarkMode ? 'border-gray-700 bg-gray-900/40 text-gray-300' : 'border-gray-200 bg-white text-gray-700'}`}>
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading saved attachments…
          </div>
        )}

        {/* Photo Grid */}
        {photoAttachments.length > 0 && (
          <div className="grid grid-cols-1 gap-3">
            {photoAttachments.map((photo, photoIdx) => {
              const previewSource = photo.data_url || photo.file_path;
              const attachmentConfidence = getAttachmentConfidence(photo);
              const fileSize = photo.file_size ?? photo.file_blob?.size ?? 0;
              const uploadStatusLabel = photo.upload_status === 'uploaded'
                ? 'Saved to document'
                : photo.upload_status === 'uploading'
                  ? 'Saving…'
                  : photo.upload_status === 'failed'
                    ? 'Needs retry'
                    : 'Pending save';
              const isActive = photoIdx === clampedPhotoIdx;
              const previewIdx = photoIdxToPreviewIdx[photoIdx];

              return (
                <div
                  key={photo.id}
                  onClick={() => {
                    setActivePhotoIdx(photoIdx);
                    if (previewIdx != null) {
                      // Scroll the viewer into view
                      const viewer = document.querySelector('[data-photo-viewer]');
                      viewer?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                  }}
                  className={`relative rounded-lg overflow-hidden border cursor-pointer transition-all ${isActive ? 'ring-2 ring-emerald-500 ' : ''}${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}
                >
                  {previewSource && isImageLikeAttachment(photo.file_name, photo.mime_type) ? (
                    <img
                      src={previewSource}
                      alt={photo.file_name}
                      className="w-full h-32 object-cover"
                    />
                  ) : (
                    <div className={`flex h-32 flex-col items-center justify-center px-4 text-center ${isDarkMode ? 'bg-gray-900 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                      <FileText className="h-8 w-8 mb-2 opacity-70" />
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em]">
                        {photo.file_type || 'FILE'}
                      </p>
                      <p className="mt-1 line-clamp-2 text-[10px] opacity-80">{photo.file_name}</p>
                    </div>
                  )}

                  {/* OCR Processing Indicator */}
                  {photo.ocr_processing && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="text-center">
                        <Loader2 className="h-6 w-6 text-white animate-spin mx-auto mb-1" />
                        <p className="text-white text-xs font-medium">Analyzing…</p>
                      </div>
                    </div>
                  )}

                  {/* OCR Success Badge */}
                  {(photo.ocr_result || photo.structured_result || photo.sales_result) && !photo.ocr_processing && (
                    <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1 ${photo.sales_result ? 'bg-pink-600 text-white' : photo.ocr_result ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white'}`}>
                      <Sparkles className="h-3 w-3" />
                      {photo.sales_result ? 'Sales mapped' : photo.ocr_result ? 'AI Complete' : 'Parsed'}
                    </div>
                  )}

                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={() => void onRemovePhoto(photo.id)}
                    className="absolute top-2 left-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={photo.ocr_processing || photo.upload_status === 'uploading'}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>

                  {/* Photo Info */}
                  <div className={`p-2.5 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className={`text-[11px] truncate font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {photo.file_name}
                        </p>
                        <p className={`mt-0.5 text-[9px] uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          {photo.file_type || 'file'}
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          <span className={`rounded-full px-1.5 py-0.5 text-[9px] ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                            {formatAttachmentFileSize(fileSize)}
                          </span>
                          <span className={`rounded-full px-1.5 py-0.5 text-[9px] ${photo.upload_status === 'failed'
                            ? (isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700 border border-red-100')
                            : photo.upload_status === 'uploaded'
                              ? (isDarkMode ? 'bg-violet-900/30 text-violet-300' : 'bg-violet-50 text-violet-700 border border-violet-100')
                              : (isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700 border border-slate-200')
                          }`}>
                            {uploadStatusLabel}
                          </span>
                          {attachmentConfidence != null && (
                            <span className={`rounded-full px-1.5 py-0.5 text-[9px] ${isDarkMode ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'}`}>
                              {buildDetectedSignalLabel(attachmentConfidence)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {photo.upload_error && (
                      <div className={`mt-1.5 rounded-lg px-2 py-1 text-[10px] ${isDarkMode ? 'bg-red-900/30 text-red-200' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                        {photo.upload_error}
                      </div>
                    )}

                    {photo.analysis_error && (
                      <div className={`mt-1.5 rounded-lg px-2 py-1 text-[10px] ${isDarkMode ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                        {photo.analysis_error}
                      </div>
                    )}

                    {/* Sales result actions */}
                    {photo.sales_result && (
                      <div className="mt-2 space-y-1.5">
                        <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                          <div className={`rounded-lg px-2 py-1 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                            <span className="block text-[9px] uppercase tracking-wide opacity-70">Source</span>
                            <span className="font-semibold">{getSalesIntakeSourceLabel(photo.sales_result.source_document_type)}</span>
                          </div>
                          <div className={`rounded-lg px-2 py-1 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                            <span className="block text-[9px] uppercase tracking-wide opacity-70">Next</span>
                            <span className="font-semibold text-pink-600">{getSalesIntakeTargetLabel(photo.sales_result.recommended_target)}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {photo.sales_result.customer_po_number && (
                            <button
                              type="button"
                              onClick={() => void onApplySalesCustomerPo(photo.id)}
                              className={`inline-flex items-center rounded-lg px-2 py-1 text-[10px] font-semibold transition-colors ${isDarkMode ? 'bg-pink-900/30 text-pink-300 hover:bg-pink-900/50' : 'bg-pink-50 text-pink-700 hover:bg-pink-100'}`}
                            >
                              Apply PO
                            </button>
                          )}
                          {photo.sales_result.requested_date && (
                            <button
                              type="button"
                              onClick={() => onApplyOcrDate(photo.id)}
                              className={`inline-flex items-center rounded-lg px-2 py-1 text-[10px] font-semibold transition-colors ${isDarkMode ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                            >
                              Apply date
                            </button>
                          )}
                          {photo.sales_result.total_amount != null && (
                            <button
                              type="button"
                              onClick={() => void onApplyOcrTotal(photo.id)}
                              className={`inline-flex items-center rounded-lg px-2 py-1 text-[10px] font-semibold transition-colors ${isDarkMode ? 'bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                            >
                              Apply total
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => void onRerunAttachmentAnalysis(photo.id)}
                            className={`inline-flex items-center rounded-lg px-2 py-1 text-[10px] font-semibold transition-colors ${isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'}`}
                          >
                            Re-analyze
                          </button>
                        </div>
                      </div>
                    )}

                    {/* OCR result actions */}
                    {photo.ocr_result && !photo.sales_result && (
                      <div className="mt-2 space-y-1.5">
                        <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                          <div className={`rounded-lg px-2 py-1 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                            <span className="block text-[9px] uppercase tracking-wide opacity-70">Total</span>
                            <span className="font-semibold text-emerald-600">
                              {photo.ocr_result.total_amount != null
                                ? formatCurrencyWithSymbol(photo.ocr_result.total_amount)
                                : 'Not found'}
                            </span>
                          </div>
                          <div className={`rounded-lg px-2 py-1 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                            <span className="block text-[9px] uppercase tracking-wide opacity-70">Date</span>
                            <span className="font-semibold">
                              {photo.ocr_result.date || 'Not found'}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => void onApplyOcrTotal(photo.id)}
                            className={`inline-flex items-center rounded-lg px-2 py-1 text-[10px] font-semibold transition-colors ${isDarkMode ? 'bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                          >
                            Apply total
                          </button>
                          <button
                            type="button"
                            onClick={() => onApplyOcrDate(photo.id)}
                            className={`inline-flex items-center rounded-lg px-2 py-1 text-[10px] font-semibold transition-colors ${isDarkMode ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                          >
                            Use date
                          </button>
                          <button
                            type="button"
                            onClick={() => void onRerunAttachmentAnalysis(photo.id)}
                            className={`inline-flex items-center rounded-lg px-2 py-1 text-[10px] font-semibold transition-colors ${isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'}`}
                          >
                            Re-analyze OCR
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Structured result actions */}
                    {photo.structured_result && (
                      <div className="mt-2 space-y-1.5">
                        <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                          <div className={`rounded-lg px-2 py-1 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                            <span className="block text-[9px] uppercase tracking-wide opacity-70">Format</span>
                            <span className="font-semibold">{photo.structured_result.format}</span>
                          </div>
                          <div className={`rounded-lg px-2 py-1 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                            <span className="block text-[9px] uppercase tracking-wide opacity-70">Rows</span>
                            <span className="font-semibold">{photo.structured_result.parsed_rows}/{photo.structured_result.total_rows}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(photo.structured_result.end_date || photo.structured_result.start_date) && (
                            <button
                              type="button"
                              onClick={() => onApplyOcrDate(photo.id)}
                              className={`inline-flex items-center rounded-lg px-2 py-1 text-[10px] font-semibold transition-colors ${isDarkMode ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                            >
                              Use file date
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => void onRerunAttachmentAnalysis(photo.id)}
                            className={`inline-flex items-center rounded-lg px-2 py-1 text-[10px] font-semibold transition-colors ${isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'}`}
                          >
                            Re-analyze
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {photoAttachments.length === 0 && !loadingPersistedAttachments && (
          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <Camera className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-xs">No files attached yet</p>
            <p className="text-[10px] mt-1">
              {isPurchaseDocument
                ? 'Drop images, PDFs, or bank files to start analysis.'
                : 'Drop POs, quotes, or spreadsheets to map sales context.'}
            </p>
          </div>
        )}

        {/* OCR Processing Global Status */}
        {ocrProcessing && (
          <div className={`mt-3 p-2 rounded-lg ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
            <div className="flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span className="text-xs">Processing uploaded files…</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
